import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { trackNavigation, trackContactClick, trackPageView } from '../utils/analytics';
import { navigateToSection } from '../hooks/useSectionUrlSync';

// `slug` is the section-aliased URL (e.g. `/design`); `sectionId` is the
// matching DOM `id` on SinglePageLayout. The two are synced by
// `useSectionUrlSync` so scrolling silently updates the URL.
const navLinks = [
  { label: 'HOME', slug: '', sectionId: 'home' },
  { label: 'DESIGN', slug: 'design', sectionId: 'design' },
  { label: 'CONCEPT', slug: 'concept', sectionId: 'concept' },
  { label: 'AMENITIES', slug: 'amenities', sectionId: 'amenities' },
  { label: 'LOCATION', slug: 'location', sectionId: 'location' },
  { label: 'PRICING', slug: 'pricing', sectionId: 'pricing' },
  { label: 'NEWS & EVENTS', slug: 'news-events', sectionId: 'news' },
];

const SECTION_PATHS = new Set(['', '/', '/design', '/concept', '/amenities', '/location', '/pricing', '/news-events', '/contact']);

const Navbar = ({ onContactClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const navigate = useNavigate();

  // Scroll spy effect - detect which section is currently in view
  const handleScrollSpy = useCallback(() => {
    // Run scroll spy on all section-aliased paths (handled by SinglePageLayout)
    if (!SECTION_PATHS.has(location.pathname)) return;

    const sections = ['home', 'design', 'concept', 'amenities', 'location', 'pricing', 'news', 'contact'];
    const scrollPosition = window.scrollY + 150; // Offset for navbar height

    for (let i = sections.length - 1; i >= 0; i--) {
      const section = document.getElementById(sections[i]);
      if (section) {
        const sectionTop = section.offsetTop;
        if (scrollPosition >= sectionTop) {
          setActiveSection(sections[i]);
          break;
        }
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      handleScrollSpy();
    };
    
    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScrollSpy();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScrollSpy]);

  // Track page views on route change
  useEffect(() => {
    const pageNames = {
      '/': 'Home',
      '/news': 'News & Events',
    };
    trackPageView(location.pathname, pageNames[location.pathname] || 'Unknown');
  }, [location.pathname]);

  const handleContactClick = () => {
    trackContactClick('header');
    if (onContactClick) {
      onContactClick();
    }
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const navbarHeight = 80;
      const sectionTop = section.offsetTop - navbarHeight;
      window.scrollTo({
        top: sectionTop,
        behavior: 'smooth'
      });
    }
  };

  const handleNavClick = (link, isMobile = false) => {
    trackNavigation(link.label, isMobile ? 'mobile_menu' : 'header');

    if (isMobile) {
      setIsMobileMenuOpen(false);
    }

    // If user is on a non-section route (e.g. /privacy-policy), use react
    // router to navigate to the slug-aliased home route, then let the layout
    // handle scrolling.
    if (!SECTION_PATHS.has(location.pathname)) {
      navigate('/' + link.slug);
      return;
    }

    // Already on the home page (any of its slug aliases) — scroll & push history
    navigateToSection(link.slug);
  };

  const isActive = (link) => {
    if (SECTION_PATHS.has(location.pathname)) {
      return activeSection === link.sectionId;
    }
    return false;
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md py-2' : 'bg-white py-3'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex justify-between items-center">
            {/* Logo - Using SVG logo */}
            <div className="flex-shrink-0">
              <Link 
                to="/"
                className="flex items-stretch gap-2 sm:gap-3"
                onClick={(e) => {
                  if (location.pathname === '/') {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                data-testid="navbar-logo-link"
              >
                {/* MEMCO logo (includes MEMCO SKYLINE wordmark baked in) */}
                <img 
                  src="/images/logo/memco-logo.svg" 
                  alt="MEMCO SKYLINE" 
                  className="h-9 sm:h-11 md:h-[52px] w-auto"
                  loading="lazy" 
                  style={{ 
                    mixBlendMode: 'multiply',
                    filter: 'contrast(1.2)'
                  }}
                />

                {/* JUTAKU combined kanji + wordmark logo, separated by a thin divider */}
                <div className="flex items-center border-l border-gray-300 pl-2 sm:pl-3">
                  <img
                    src="/images/logo/jutaku-kan-eng-black.svg"
                    alt="JUTAKU"
                    className="h-8 sm:h-9 md:h-11 w-auto"
                    loading="lazy"
                    style={{
                      mixBlendMode: 'multiply',
                      filter: 'contrast(1.2)',
                      backgroundColor: 'transparent'
                    }}
                    data-testid="navbar-jutaku-logo"
                  />
                </div>

                {/* Urban Lifestyle Ecosystem descriptor — 3-line stack matching brand identity */}
                <span className="hidden md:inline-block text-[11px] lg:text-[12px] font-semibold tracking-[0.2em] text-gray-800 uppercase leading-[1.45] border-l border-gray-300 pl-3 self-center whitespace-nowrap">
                  Urban<br />Lifestyle<br />Ecosystem
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-5 xl:space-x-7 whitespace-nowrap">
              {navLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={() => handleNavClick(link, false)}
                  className={`text-[15px] font-medium transition-colors tracking-wide ${
                    isActive(link)
                      ? 'text-[#008751]'
                      : 'text-gray-800 hover:text-[#008751]'
                  }`}
                  data-testid={`nav-link-${link.slug || 'home'}`}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={handleContactClick}
                className="bg-[#1a3a2a] text-white px-6 py-2.5 text-[15px] font-medium tracking-wide hover:bg-[#008751] hover:scale-105 transition-all duration-300 rounded-full shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                CONTACT NOW
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-gray-800 bg-white rounded-md shadow-md border border-gray-200"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link, index) => (
                <button
                  key={index}
                  onClick={() => handleNavClick(link, true)}
                  className={`block w-full text-left text-sm font-medium transition-colors tracking-wide py-2 ${
                    isActive(link)
                      ? 'text-[#008751]'
                      : 'text-gray-700 hover:text-[#008751]'
                  }`}
                  data-testid={`nav-link-mobile-${link.slug || 'home'}`}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={handleContactClick}
                className="w-full bg-[#1a3a2a] text-white px-6 py-3 text-sm font-medium tracking-wide hover:bg-[#008751] transition-all duration-300 mt-4 rounded-full shadow-lg"
              >
                CONTACT NOW
              </button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
